import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import {
  Flex,
  Box,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Button,
  Stack,
  useToast,
  Divider,
  Text,
  InputGroup,
  InputRightElement,
  IconButton,
  Icon,
  Progress,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { ViewIcon, ViewOffIcon, CheckCircleIcon, WarningIcon } from '@chakra-ui/icons';

const MotionBox = motion(Box);

const ResetPasswordPage = () => {
  const { uid, token } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordStrengthText, setPasswordStrengthText] = useState('');
  const [timeLeft, setTimeLeft] = useState(300); 
  const [isLinkExpired, setIsLinkExpired] = useState(false);

  useEffect(() => {
    if (timeLeft > 0 && !isLinkExpired) {
      const timer = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && !isLinkExpired) {
      setIsLinkExpired(true);
      toast({
        title: 'Link Expired',
        description: 'This password reset link has expired. Please request a new one.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
     
    }
  }, [timeLeft, isLinkExpired, navigate, toast]);

  useEffect(() => {
    setPasswordsMatch(newPassword === confirmPassword);
  }, [newPassword, confirmPassword]);

  const checkPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (password.match(/[a-z]/)) strength += 25;
    if (password.match(/[A-Z]/)) strength += 25;
    if (password.match(/[0-9]/)) strength += 25;
    if (password.match(/[^a-zA-Z0-9]/)) strength += 25; 

    let text = '';
    let value = 0;
    if (strength < 50) {
      text = 'Weak';
      value = 25;
    } else if (strength < 75) {
      text = 'Moderate';
      value = 50;
    } else if (strength < 100) {
      text = 'Strong';
      value = 75;
    } else if (strength === 100) {
      text = 'Very Strong';
      value = 100;
    }
    return { value, text };
  };

  const getPasswordStrengthColor = (strength) => {
    if (strength < 50) return 'red';
    if (strength < 75) return 'yellow';
    return 'green';
  };

  const handleNewPasswordChange = (e) => {
    const newPass = e.target.value;
    setNewPassword(newPass);
    const strength = checkPasswordStrength(newPass);
    setPasswordStrength(strength.value);
    setPasswordStrengthText(strength.text);
  };

  const handleShowNewPassword = () => setShowNewPassword(!showNewPassword);
  const handleShowConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

  const verifyLinkAndResetPassword = async () => {
    if (!passwordsMatch) {
      toast({
        title: 'Passwords do not match',
        description: 'Please ensure both passwords are the same.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(
        'https://gyanbackend.aim4u.co.in/reset-password-confirm/', 
        {
          uid: uid,
          token: token,
          new_password: newPassword,
        }
      );

      if (response.data.message === 'Password has been reset successfully.') {
        toast({
          title: 'Password Reset Successful',
          description: 'You can now log in with your new password.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        navigate('/login'); 
      } else {
        toast({
          title: 'Password Reset Failed',
          description:
            response.data.message || 'Invalid reset link or an error occurred.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        if (response.data.message === 'Invalid or expired token.') {
          setIsLinkExpired(true);
          setTimeLeft(0); 
        }
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Something went wrong. Please try again later.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  return (
    <Flex
      bg="gray.50"
      minH="100vh"
      align="center"
      justify="center"
      py={12}
      bgGradient="linear(to-br, gray.100, white)" 
    >
      <MotionBox
        bg="white"
        boxShadow="xl"
        rounded="md"
        p={{ base: 6, md: 8 }}
        width={{ base: '90%', md: '400px' }}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Heading
          as="h2"
          size="lg"
          fontWeight="semibold"
          color="#006dd4"
          textAlign="center"
          mb={4}
        >
          Reset Your Password
        </Heading>
        {!isLinkExpired && (
          <Text textAlign="center" color="gray.600" fontSize="sm" mb={2}>
            This password reset link is valid for the next {formatTime(timeLeft)}.
          </Text>
        )}
        {isLinkExpired && (
          <Text textAlign="center" color="red.500" fontSize="sm" mb={4}>
            This link has expired. Please request a new one.
          </Text>
        )}

        <Stack spacing={4} isDisabled={isLinkExpired}>
          <FormControl id="new-password" isDisabled={isLinkExpired}>
            <FormLabel fontWeight="medium">New Password</FormLabel>
            <InputGroup>
              <Input
                type={showNewPassword ? 'text' : 'password'}
                placeholder="Enter your new password"
                bg="gray.100"
                border="none"
                _focus={{ bg: 'gray.200' }}
                value={newPassword}
                onChange={handleNewPasswordChange}
                isDisabled={isLinkExpired}
              />
              <InputRightElement>
                <IconButton
                  h="1.75rem"
                  size="sm"
                  onClick={handleShowNewPassword}
                  icon={showNewPassword ? <ViewOffIcon /> : <ViewIcon />}
                  aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                  isDisabled={isLinkExpired}
                />
              </InputRightElement>
            </InputGroup>
            {newPassword && (
              <Box mt={2}>
                <Progress value={passwordStrength} colorScheme={getPasswordStrengthColor(passwordStrength)} />
                <Text fontSize="sm" color="gray.500">{passwordStrengthText}</Text>
              </Box>
            )}
          </FormControl>

          <FormControl id="confirm-password" isDisabled={isLinkExpired}>
            <FormLabel fontWeight="medium">Confirm New Password</FormLabel>
            <InputGroup>
              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm your new password"
                bg="gray.100"
                border="none"
                _focus={{ bg: 'gray.200' }}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                isDisabled={isLinkExpired}
              />
              {confirmPassword && (
                <InputRightElement>
                  <Icon
                    as={passwordsMatch ? CheckCircleIcon : WarningIcon}
                    color={passwordsMatch ? 'green.500' : 'red.500'}
                  />
                </InputRightElement>
              )}
            </InputGroup>
            {!passwordsMatch && confirmPassword && (
              <Text fontSize="sm" color="red.500" mt={1}>Passwords do not match</Text>
            )}
          </FormControl>

          <Button
            bg="#006dd4"
            color="white"
            _hover={{ bg: 'blue.600' }}
            fontWeight="semibold"
            isLoading={isLoading}
            onClick={verifyLinkAndResetPassword}
            isDisabled={isLinkExpired || !passwordsMatch || newPassword.length < 6} 
          >
            Reset Password
          </Button>
          {!passwordsMatch && newPassword && confirmPassword && (
            <Text textAlign="center" color="red.500" fontSize="sm">
              Passwords do not match.
            </Text>
          )}
          {newPassword.length < 6 && newPassword && (
            <Text textAlign="center" color="orange.500" fontSize="sm">
              Password must be at least 6 characters long.
            </Text>
          )}
        </Stack>

        <Divider my={6} borderColor="gray.300" />

        <Text textAlign="center" color="gray.600" fontSize="sm">
          Having trouble?{' '}
          <RouterLink to={`mailto:gyan.aim4u@gmail.com`} color="#006dd4">
            Contact support
          </RouterLink>
        </Text>
        <Text textAlign="center" mt={2} fontSize="sm">
          <RouterLink to="/login" color="blue.500">
            Back to Login
          </RouterLink>
        </Text>
      </MotionBox>
    </Flex>
  );
};

export default ResetPasswordPage;